/**
 * ehr#724 (ADMIN_PHONE_NOTE_10-30-V7, Jay 10-30: "Make this just another note type, which is
 * non-billable"): what the Admin / phone note leaves out or words its own way, in one place, as
 * V7's one ADMIN_ONLY flag does. The note is Session details and Contact note (noteLayouts) with
 * nothing about billing; the rest of the editor is the progress note's.
 */
import { QUICK_BUILTIN, type QuickItem, type QuickSideCard } from '@ehr/shared';
import { splitSentences } from './notepilot.js';

export const ADMIN_PHONE_KIND = 'Admin / phone';
/** V7 ADMIN_ONLY: this note is an Admin / phone note. */
export const isAdminPhoneKind = (kind: string): boolean => kind === ADMIN_PHONE_KIND;

/** V7 (V2 set_phrases): the Contact note's phrase pills, admin wording instead of therapy pills. */
export const ADMIN_PHONE_PHRASES: readonly string[] = [
  'Called client about __.',
  'Left a voicemail asking the client to call back.',
  'Rescheduled the session to __.',
  'Sent __ to the client.',
  'Spoke with __ about __.',
];

/** V7 (V2): the Generate Notes box's placeholder, for admin work (no session, no interventions). */
export const ADMIN_ROUGH_PLACEHOLDER =
  'Type, paste or dictate what happened. AI writes the contact note.';

/** V7 (V3, 12a): the Session details row is Type and place, with no billing in it. */
export const typePlaceLabel = (kind: string): string =>
  isAdminPhoneKind(kind) ? 'Type and place' : 'Type, place and billing';

/**
 * V7 (V4 bug pass, 1): Generate Notes writes the Contact note from what was typed, each sentence
 * started with a capital and ended with a period, never a canned therapy line. Nothing typed
 * writes nothing.
 */
export function adminContactDraft(summary: string): { subjective: string } {
  const subjective = splitSentences(summary)
    .map((s) => {
      const t = s.replace(/\s+/g, ' ').trim();
      const c = t.charAt(0).toUpperCase() + t.slice(1);
      return /[.!?]$/.test(c) ? c : `${c}.`;
    })
    .join(' ');
  return { subjective };
}

/**
 * V7 (V5, 14a): Quick add > Ratings starts empty on an Admin / phone note. The built-in ratings
 * (Mood, Anxiety, Sleep and the rest, and Session) are not offered; a rating you add yourself is.
 */
export const isBuiltInRating = (it: Pick<QuickItem, 'id' | 'group'>): boolean =>
  it.group === 'ratings' && QUICK_BUILTIN.some((b) => b.id === it.id);

/**
 * Quick add settings, a row dragged (or moved with its arrow keys) to place `to` of the rows the
 * tab lists (`shown`), as the whole saved order of that group (`full`). Rows the tab doesn't list
 * (the built-in ratings on an Admin / phone note, Relationship satisfaction off a couples note)
 * keep their places, so another note's order isn't changed. null when nothing moves.
 */
export function moveShown(
  full: readonly string[],
  shown: readonly string[],
  id: string,
  to: number,
): string[] | null {
  const all = [...shown];
  const from = all.indexOf(id);
  if (from < 0 || to < 0 || to >= all.length || from === to) return null;
  all.splice(to, 0, ...all.splice(from, 1));
  let k = 0;
  return full.map((x) => (shown.includes(x) ? all[k++]! : x));
}

/**
 * V7: the rail cards off on this note: the ones switched off in Note settings > Quick add, and
 * on an Admin / phone note always Measures (nothing is scored on an admin note).
 */
export function railCardsOff(kind: string, hidden: readonly QuickSideCard[]): QuickSideCard[] {
  return isAdminPhoneKind(kind) && !hidden.includes('measures')
    ? [...hidden, 'measures']
    : [...hidden];
}
